import type { Pricing } from '#shared/types';
import { PaymentStatusBadge } from '#client/components/ui/StatusBadge';
import { formatMoney } from '#client/lib/format';

/**
 * The derived bill, as the operator sees it before it is saved.
 *
 * Shared by the registration form and the charge calculator. Those two rendered
 * near-identical `<dl className="c-summary">` blocks by hand, which was
 * tolerable while the bill was three lines and stopped being so the moment tax
 * and extra charges arrived — the same de-duplication argument that put
 * `calculatePricing` in `shared/pricing.ts` in the first place. One component
 * means a line cannot appear on one screen and be forgotten on the other.
 *
 * ## It renders `Pricing` and nothing else
 *
 * Every figure comes from the one object `calculatePricing` returned. The
 * captions need the raw inputs — a discount is "15%" or "Rs 150" depending on
 * which box was typed in — but no number on screen is re-derived here. That
 * matters because the same rule governs the printed slip: a line that is worked
 * out by subtracting two other lines is the line that goes wrong when a term is
 * added to the bill.
 */
interface BillingSummaryProps {
  pricing: Pricing;
  /** What the operator typed, for the discount caption only. */
  discountRupees: number;
  discountPercentage: number;
  taxPercentage: number;
  otherChargesLabel?: string | null | undefined;
  /**
   * Show the derived payment status.
   *
   * Off for the calculator: nothing is being booked, so "Pending" would be a
   * judgement about a quote nobody has agreed to.
   */
  showStatus?: boolean | undefined;
}

export function BillingSummary({
  pricing,
  discountRupees,
  discountPercentage,
  taxPercentage,
  otherChargesLabel,
  showStatus = false,
}: BillingSummaryProps) {
  return (
    <dl className="c-summary">
      <Row label="Subtotal" value={formatMoney(pricing.subtotal)} />

      <Row
        label={`Discount (${discountLabel(pricing.discount_kind, discountRupees, discountPercentage)})`}
        value={`−${formatMoney(pricing.discount)}`}
      />

      {/* Zero lines are omitted rather than shown as "Rs 0", matching the slip.
          A tax line on a bill that was not taxed invites the question. */}
      {pricing.tax > 0 && (
        <Row label={`Sales tax (${taxPercentage}%)`} value={`+${formatMoney(pricing.tax)}`} />
      )}

      {pricing.other_charges > 0 && (
        <Row
          label={otherChargesLabel?.trim() || 'Other charges'}
          value={`+${formatMoney(pricing.other_charges)}`}
        />
      )}

      <div className="c-summary__row c-summary__row--total">
        <dt>Total</dt>
        <dd>{formatMoney(pricing.total)}</dd>
      </div>

      <Row label="Balance due" value={formatMoney(pricing.balance)} />

      {showStatus && (
        // Shown rather than chosen. A record marked "Paid" with a balance
        // outstanding is a lie the outstanding-balance tile and the attention
        // feed both read, so the status follows from what is owed instead of
        // from a dropdown.
        <div className="c-summary__row">
          <dt>Payment status</dt>
          <dd>
            <PaymentStatusBadge status={pricing.payment_status} />
          </dd>
        </div>
      )}
    </dl>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="c-summary__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** The discount line's caption. Formatting is a screen concern, not pricing's. */
function discountLabel(kind: Pricing['discount_kind'], rupees: number, percentage: number): string {
  if (kind === 'percentage') return `${percentage}%`;
  if (kind === 'rupees') return formatMoney(rupees);
  return '—';
}
